import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { FileType } from "Types";
import {
  useFilestreamDuplicateCheckQuery,
  usePackagesDuplicateCheckQuery,
} from "../services/upload.api";
import { HashFileRequest } from "../types";
import { getFileChunksCount } from "../utils";
import { createNewWebWorker } from "./hashWebWorker";

const MAX_RETRIES = 3;

function useCheckFileDuplicates(
  uploadedFile: File | undefined,
  uploadType: FileType,
) {
  const webWorkerRef = useRef<Worker | null>(null);
  const webWorkerChunksProcessedRef = useRef<number[]>([]);
  const webWorkerRetryCountRef = useRef(0);

  const [hashingError, setHashingError] = useState<string | null>(null);
  const [fileHash, setFileHash] = useState<string | null>(null);
  const [fileChunksProcessedCount, setFileChunksProcessedCount] = useState(0);
  const { groupId, packageId } = useParams();

  const fileChunksTotalCount = uploadedFile
    ? getFileChunksCount(uploadedFile)
    : 0;

  useEffect(() => {
    // Reset state
    setFileHash(null);
    setFileChunksProcessedCount(0);
    setHashingError(null);
    webWorkerChunksProcessedRef.current = [];
    webWorkerRetryCountRef.current = 0;

    // Reinitialize web worker
    if (webWorkerRef.current) {
      webWorkerRef.current.terminate();
    }

    webWorkerRef.current = createNewWebWorker();

    webWorkerRef.current.addEventListener("message", (event: MessageEvent) => {
      const message = event.data;

      if (message.type === "file_chunk_processed") {
        const chunk = message.payload;
        const { chunkIndex } = chunk;

        if (webWorkerChunksProcessedRef.current.includes(chunkIndex)) return;

        webWorkerChunksProcessedRef.current = [
          ...webWorkerChunksProcessedRef.current,
          chunkIndex,
        ];
        setFileChunksProcessedCount(webWorkerChunksProcessedRef.current.length);
      } else if (message.type === "hash_file") {
        const hash = message.payload;
        if (typeof hash === "string") {
          setFileHash(hash);
        }
      } else if (message.type === "error") {
        if (webWorkerRetryCountRef.current < MAX_RETRIES) {
          setFileChunksProcessedCount(0);
          webWorkerChunksProcessedRef.current = [];
          webWorkerRetryCountRef.current++;

          // Retry hashing
          webWorkerRef.current?.postMessage({
            type: "hash_file",
            payload: { data: uploadedFile },
          } as HashFileRequest);
        } else {
          setHashingError(message.payload.error);
        }
      }
    });

    if (!uploadedFile || !webWorkerRef.current) return;

    // If a new file was uploaded, start hashing
    webWorkerRef.current.postMessage({
      type: "hash_file",
      payload: { data: uploadedFile },
    } as HashFileRequest);

    return () => {
      if (webWorkerRef.current) {
        webWorkerRef.current.terminate();
        webWorkerRef.current = null;
      }
    };
  }, [uploadedFile]);

  const fileHashNotGenerated = !uploadedFile || !fileHash;

  const duplicateCheckResponse = useFilestreamDuplicateCheckQuery(
    { sha256: fileHash ?? "", groupId: groupId ?? "" },
    {
      skip: uploadType !== "FILESTREAM" || !groupId || fileHashNotGenerated,
    },
  );

  const packagesDuplicateCheckData = usePackagesDuplicateCheckQuery(
    { sha256: fileHash ?? "", packageId: packageId ?? "" },
    {
      skip: uploadType !== "VERSION" || !packageId || fileHashNotGenerated,
    },
  );

  const isGeneratingHash = uploadedFile && !fileHash;

  const data = useMemo(() => {
    const progress =
      fileChunksTotalCount > 0
        ? Math.round((fileChunksProcessedCount / fileChunksTotalCount) * 100)
        : 100;

    return {
      ...(uploadType === "FILESTREAM"
        ? duplicateCheckResponse
        : packagesDuplicateCheckData),
      isGeneratingHash,
      fileHash,
      progress: packagesDuplicateCheckData.isSuccess ? 100 : progress,
      hashingError,
    };
  }, [
    duplicateCheckResponse,
    packagesDuplicateCheckData,
    isGeneratingHash,
    fileHash,
    uploadType,
    fileChunksProcessedCount,
    fileChunksTotalCount,
    hashingError,
  ]);

  return data;
}

export type FileDuplicateCheck = ReturnType<typeof useCheckFileDuplicates>;

export default useCheckFileDuplicates;
